import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { ArrowLeft, ArrowRight, CheckCircle, Building2, Target, Users, Calendar, Wrench, Award, DollarSign, Phone } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import confetti from 'canvas-confetti';

const TrainingAssessment = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Section 1
    companyName: '',
    activitySector: '',
    companySize: '',
    location: '',
    contactName: '',
    contactPosition: '',
    contactEmail: '',
    contactPhone: '',
    
    // Section 2
    interestDomains: [] as string[],
    mainObjectives: [] as string[],
    desiredLevel: '',
    
    // Section 3
    targetAudience: [] as string[],
    participantsCount: '',
    
    // Section 4
    preferredFormat: '',
    duration: '',
    desiredPeriod: '',
    
    // Section 5
    desiredTools: [] as string[],
    
    // Section 6
    expectations: [] as string[],
    currentLevel: '',
    
    // Section 7
    existingBudget: '',
    opcoFinancing: '',
    
    // Section 8
    followUpPreferences: [] as string[],
    quoteEmail: '',
    callbackPhone: '',
    additionalNotes: ''
  });

  const totalSteps = 8;

  const handleCheckboxChange = (field: keyof typeof formData, value: string) => {
    const currentValues = formData[field] as string[];
    setFormData({
      ...formData,
      [field]: currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value]
    });
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('training_needs_assessments')
        .insert({
          company_name: formData.companyName,
          activity_sector: formData.activitySector,
          company_size: formData.companySize,
          location: formData.location,
          contact_name: formData.contactName,
          contact_position: formData.contactPosition,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          interest_domains: formData.interestDomains,
          main_objectives: formData.mainObjectives,
          desired_level: formData.desiredLevel,
          target_audience: formData.targetAudience,
          participants_count: formData.participantsCount,
          preferred_format: formData.preferredFormat,
          duration: formData.duration,
          desired_period: formData.desiredPeriod,
          desired_tools: formData.desiredTools,
          expectations: formData.expectations,
          current_level: formData.currentLevel,
          existing_budget: formData.existingBudget,
          opco_financing: formData.opcoFinancing,
          follow_up_preferences: formData.followUpPreferences,
          quote_email: formData.quoteEmail,
          callback_phone: formData.callbackPhone,
          additional_notes: formData.additionalNotes
        });

      if (error) throw error;

      triggerConfetti();
      setCurrentStep(9); // Success step
      
      toast({
        title: "Demande envoyée avec succès !",
        description: "Nous avons bien reçu votre demande et nous vous contacterons rapidement.",
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIcons = [Building2, Target, Users, Calendar, Wrench, Award, DollarSign, Phone];
  const StepIcon = stepIcons[currentStep - 1];

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.companyName && formData.activitySector && formData.companySize && 
               formData.location && formData.contactName && formData.contactPosition && formData.contactEmail;
      case 2:
        return formData.interestDomains.length > 0 && formData.mainObjectives.length > 0 && formData.desiredLevel;
      case 3:
        return formData.targetAudience.length > 0 && formData.participantsCount;
      case 4:
        return formData.preferredFormat && formData.duration && formData.desiredPeriod;
      case 5:
        return formData.desiredTools.length > 0;
      case 6:
        return formData.expectations.length > 0 && formData.currentLevel;
      case 7:
        return formData.existingBudget && formData.opcoFinancing;
      case 8:
        return formData.followUpPreferences.length > 0;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-20 pb-12 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-tech bg-clip-text text-transparent">
              Évaluation des Besoins en Formation IT
            </h1>
            <p className="text-lg text-muted-foreground">
              Aidez-nous à comprendre vos besoins pour vous proposer une formation sur mesure
            </p>
          </motion.div>

          {/* Progress Bar */}
          {currentStep <= 8 && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Étape {currentStep} sur {totalSteps}</span>
                <span className="text-sm text-muted-foreground">{Math.round((currentStep / totalSteps) * 100)}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-tech"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
            </div>
          )}

          {/* Form Steps */}
          <AnimatePresence mode="wait">
            {currentStep === 9 ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-center py-16"
              >
                <CheckCircle className="w-24 h-24 mx-auto mb-6 text-green-500" />
                <h2 className="text-3xl font-bold mb-4">Demande Envoyée avec Succès !</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Merci pour votre confiance. Notre équipe d'experts analysera vos besoins et vous contactera rapidement pour vous proposer une solution de formation personnalisée.
                </p>
                <div className="flex gap-4 justify-center">
                  <Button onClick={() => navigate('/academy')} size="lg">
                    Découvrir nos Formations
                  </Button>
                  <Button onClick={() => navigate('/')} variant="outline" size="lg">
                    Retour à l'Accueil
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card border border-border rounded-lg p-8 shadow-lg"
              >
                {/* Step Icon & Title */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <StepIcon className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {currentStep === 1 && "Informations Générales"}
                      {currentStep === 2 && "Besoins en Formation IT"}
                      {currentStep === 3 && "Public Concerné"}
                      {currentStep === 4 && "Organisation & Format"}
                      {currentStep === 5 && "Technologies et Outils"}
                      {currentStep === 6 && "Résultats Attendus"}
                      {currentStep === 7 && "Budget et Financement"}
                      {currentStep === 8 && "Contact et Suivi"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {currentStep === 1 && "Présentez votre entreprise"}
                      {currentStep === 2 && "Définissez vos objectifs de formation"}
                      {currentStep === 3 && "Identifiez les participants"}
                      {currentStep === 4 && "Choisissez le format souhaité"}
                      {currentStep === 5 && "Sélectionnez les technologies"}
                      {currentStep === 6 && "Précisez vos attentes"}
                      {currentStep === 7 && "Informations budgétaires"}
                      {currentStep === 8 && "Comment souhaitez-vous être contacté ?"}
                    </p>
                  </div>
                </div>

                {/* Step 1: Company Information */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="companyName">Nom de l'entreprise *</Label>
                        <Input
                          id="companyName"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                          placeholder="Votre entreprise"
                        />
                      </div>
                      <div>
                        <Label htmlFor="activitySector">Secteur d'activité *</Label>
                        <Input
                          id="activitySector"
                          value={formData.activitySector}
                          onChange={(e) => setFormData({ ...formData, activitySector: e.target.value })}
                          placeholder="Ex: Finance, Santé, Industrie..."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Taille de l'entreprise *</Label>
                        <div className="flex flex-col gap-2 mt-2">
                          {['Moins de 50', '50-250', '+250'].map((size) => (
                            <div key={size} className="flex items-center space-x-2">
                              <Checkbox
                                id={`size-${size}`}
                                checked={formData.companySize === size}
                                onCheckedChange={() => setFormData({ ...formData, companySize: size })}
                              />
                              <label htmlFor={`size-${size}`} className="text-sm cursor-pointer">{size}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="location">Localisation *</Label>
                        <Input
                          id="location"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="Ville, Pays"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactName">Contact (Nom) *</Label>
                        <Input
                          id="contactName"
                          value={formData.contactName}
                          onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                          placeholder="Prénom Nom"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contactPosition">Poste *</Label>
                        <Input
                          id="contactPosition"
                          value={formData.contactPosition}
                          onChange={(e) => setFormData({ ...formData, contactPosition: e.target.value })}
                          placeholder="Votre fonction"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactEmail">E-mail *</Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          value={formData.contactEmail}
                          onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                          placeholder="contact@entreprise.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contactPhone">Téléphone</Label>
                        <Input
                          id="contactPhone"
                          type="tel"
                          value={formData.contactPhone}
                          onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Training Needs */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Domaines d'intérêt *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'Automatisation intelligente / RPA',
                          'Agents IA autonomes',
                          'Automatiser les opérations répétitives',
                          'Réduire les coûts & délais',
                          'Sensibilisation',
                          'Approche stratégique & cadrage',
                          'Application & mise en œuvre',
                          'Formation experte / architecte'
                        ].map((domain) => (
                          <div key={domain} className="flex items-center space-x-2">
                            <Checkbox
                              id={`domain-${domain}`}
                              checked={formData.interestDomains.includes(domain)}
                              onCheckedChange={() => handleCheckboxChange('interestDomains', domain)}
                            />
                            <label htmlFor={`domain-${domain}`} className="text-sm cursor-pointer">{domain}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label className="mb-3 block">Objectifs principaux *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'Automatiser les opérations répétitives',
                          'Réduire les coûts & délais',
                          'Déployer des agents IA autonomes',
                          'Connecter les systèmes via API',
                          'Sécuriser les flux & données',
                          'Accompagner la transformation digitale'
                        ].map((objective) => (
                          <div key={objective} className="flex items-center space-x-2">
                            <Checkbox
                              id={`objective-${objective}`}
                              checked={formData.mainObjectives.includes(objective)}
                              onCheckedChange={() => handleCheckboxChange('mainObjectives', objective)}
                            />
                            <label htmlFor={`objective-${objective}`} className="text-sm cursor-pointer">{objective}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Niveau souhaité *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Sensibilisation', 'Approche stratégique & cadrage', 'Application & mise en œuvre', 'Formation experte / architecte'].map((level) => (
                          <div key={level} className="flex items-center space-x-2">
                            <Checkbox
                              id={`level-${level}`}
                              checked={formData.desiredLevel === level}
                              onCheckedChange={() => setFormData({ ...formData, desiredLevel: level })}
                            />
                            <label htmlFor={`level-${level}`} className="text-sm cursor-pointer">{level}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Target Audience */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Public visé *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {['Techniques', 'Non-tech', 'Managers', 'Mixte'].map((audience) => (
                          <div key={audience} className="flex items-center space-x-2">
                            <Checkbox
                              id={`audience-${audience}`}
                              checked={formData.targetAudience.includes(audience)}
                              onCheckedChange={() => handleCheckboxChange('targetAudience', audience)}
                            />
                            <label htmlFor={`audience-${audience}`} className="text-sm cursor-pointer">{audience}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Nombre de participants *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['1-5', '6-10', '11-20', '+20'].map((count) => (
                          <div key={count} className="flex items-center space-x-2">
                            <Checkbox
                              id={`count-${count}`}
                              checked={formData.participantsCount === count}
                              onCheckedChange={() => setFormData({ ...formData, participantsCount: count })}
                            />
                            <label htmlFor={`count-${count}`} className="text-sm cursor-pointer">{count}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Organization & Format */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div>
                      <Label>Format préféré *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Présentiel', 'Distanciel', 'Hybride'].map((format) => (
                          <div key={format} className="flex items-center space-x-2">
                            <Checkbox
                              id={`format-${format}`}
                              checked={formData.preferredFormat === format}
                              onCheckedChange={() => setFormData({ ...formData, preferredFormat: format })}
                            />
                            <label htmlFor={`format-${format}`} className="text-sm cursor-pointer">{format}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Durée *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['1 jour', '2-3 jours', '4-5 jours', 'Autre'].map((duration) => (
                          <div key={duration} className="flex items-center space-x-2">
                            <Checkbox
                              id={`duration-${duration}`}
                              checked={formData.duration === duration}
                              onCheckedChange={() => setFormData({ ...formData, duration: duration })}
                            />
                            <label htmlFor={`duration-${duration}`} className="text-sm cursor-pointer">{duration}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Période souhaitée *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Mois en cours', '3 mois', 'Plus tard'].map((period) => (
                          <div key={period} className="flex items-center space-x-2">
                            <Checkbox
                              id={`period-${period}`}
                              checked={formData.desiredPeriod === period}
                              onCheckedChange={() => setFormData({ ...formData, desiredPeriod: period })}
                            />
                            <label htmlFor={`period-${period}`} className="text-sm cursor-pointer">{period}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 5: Technologies */}
                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Outils souhaités *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'UiPath',
                          'Power Automate',
                          'Automation Anywhere',
                          'Moteurs d\'automatisation IA',
                          'Agents autonomes',
                          'API / microservices',
                          'Gateway API',
                          'Frameworks IA (ML, RL)',
                          'Sécurité (IAM, OAuth, JWT, chiffrement)'
                        ].map((tool) => (
                          <div key={tool} className="flex items-center space-x-2">
                            <Checkbox
                              id={`tool-${tool}`}
                              checked={formData.desiredTools.includes(tool)}
                              onCheckedChange={() => handleCheckboxChange('desiredTools', tool)}
                            />
                            <label htmlFor={`tool-${tool}`} className="text-sm cursor-pointer">{tool}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 6: Expected Results */}
                {currentStep === 6 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Attentes *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'Déploiement pilote',
                          'ROI mesurable',
                          'Agents autonomes en production',
                          'Gouvernance & suivi',
                          'Support post-formation',
                          'Documentation & best practices'
                        ].map((expectation) => (
                          <div key={expectation} className="flex items-center space-x-2">
                            <Checkbox
                              id={`expectation-${expectation}`}
                              checked={formData.expectations.includes(expectation)}
                              onCheckedChange={() => handleCheckboxChange('expectations', expectation)}
                            />
                            <label htmlFor={`expectation-${expectation}`} className="text-sm cursor-pointer">{expectation}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Niveau actuel *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Débutant', 'Expérimenté', 'En cours', 'Expert'].map((level) => (
                          <div key={level} className="flex items-center space-x-2">
                            <Checkbox
                              id={`current-${level}`}
                              checked={formData.currentLevel === level}
                              onCheckedChange={() => setFormData({ ...formData, currentLevel: level })}
                            />
                            <label htmlFor={`current-${level}`} className="text-sm cursor-pointer">{level}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 7: Budget */}
                {currentStep === 7 && (
                  <div className="space-y-6">
                    <div>
                      <Label>Budget existant ? *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Oui', 'Non', 'En cours'].map((budget) => (
                          <div key={budget} className="flex items-center space-x-2">
                            <Checkbox
                              id={`budget-${budget}`}
                              checked={formData.existingBudget === budget}
                              onCheckedChange={() => setFormData({ ...formData, existingBudget: budget })}
                            />
                            <label htmlFor={`budget-${budget}`} className="text-sm cursor-pointer">{budget}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Financement OPCO ? *</Label>
                      <div className="flex flex-col gap-2 mt-2">
                        {['Oui', 'Non'].map((opco) => (
                          <div key={opco} className="flex items-center space-x-2">
                            <Checkbox
                              id={`opco-${opco}`}
                              checked={formData.opcoFinancing === opco}
                              onCheckedChange={() => setFormData({ ...formData, opcoFinancing: opco })}
                            />
                            <label htmlFor={`opco-${opco}`} className="text-sm cursor-pointer">{opco}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 8: Contact & Follow-up */}
                {currentStep === 8 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-3 block">Souhaitez-vous recevoir : *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          'Proposition de roadmap',
                          'Démonstration',
                          'Étude de cas',
                          'Devis',
                          'Atelier découverte'
                        ].map((preference) => (
                          <div key={preference} className="flex items-center space-x-2">
                            <Checkbox
                              id={`preference-${preference}`}
                              checked={formData.followUpPreferences.includes(preference)}
                              onCheckedChange={() => handleCheckboxChange('followUpPreferences', preference)}
                            />
                            <label htmlFor={`preference-${preference}`} className="text-sm cursor-pointer">{preference}</label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="quoteEmail">E-mail pour devis</Label>
                        <Input
                          id="quoteEmail"
                          type="email"
                          value={formData.quoteEmail}
                          onChange={(e) => setFormData({ ...formData, quoteEmail: e.target.value })}
                          placeholder="devis@entreprise.com"
                        />
                      </div>
                      <div>
                        <Label htmlFor="callbackPhone">Téléphone pour rappel</Label>
                        <Input
                          id="callbackPhone"
                          type="tel"
                          value={formData.callbackPhone}
                          onChange={(e) => setFormData({ ...formData, callbackPhone: e.target.value })}
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="additionalNotes">Notes additionnelles</Label>
                      <Textarea
                        id="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                        placeholder="Informations supplémentaires, contraintes spécifiques..."
                        rows={4}
                      />
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                    disabled={currentStep === 1}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Précédent
                  </Button>
                  
                  {currentStep < 8 ? (
                    <Button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      disabled={!canProceed()}
                    >
                      Suivant
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={!canProceed() || isSubmitting}
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TrainingAssessment;